import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import LeadForm from './components/form/LeadForm';
import PathSelection from './components/form/PathSelection';
import PathBooking from './components/form/PathBooking';
import PathAiCall from './components/form/PathAiCall';
import ThankYou from './components/form/ThankYou';
import ShareButtons from './components/form/ShareButtons';
import { fetchFormOptions, selectPath } from './api/forms';
import { ageRuleText, type AgeGroup } from './lib/ageGroups';
import { trackEvent } from './lib/tracking';
import type { FormOption, LeadPath } from './types/forms';

type Step =
  | { name: 'form' }
  | { name: 'paths' }
  | { name: 'booking' }
  | { name: 'ai_call' }
  | { name: 'done'; variant: LeadPath; replacedCount?: number };

export default function App() {
  const { data: options, isLoading, isError, error } = useQuery({
    queryKey: ['form-options'],
    queryFn: fetchFormOptions,
  });

  const [step, setStep] = useState<Step>({ name: 'form' });
  const [leadId, setLeadId] = useState<string | null>(null);
  const [option, setOption] = useState<FormOption | null>(null);
  // Altersgruppe des Kindes („Für mein Kind"), sonst null.
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  // Ab 15 in Erwachsenenkursen: Die Eltern müssen den Vertrag unterschreiben.
  const [parentsMustCome, setParentsMustCome] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  // opt = null → keine konkrete Sportart gewählt → nur Rückruf möglich.
  const handleCaptured = (
    id: string,
    opt: FormOption | null,
    group: AgeGroup | null,
    withParents: boolean,
  ) => {
    setLeadId(id);
    setOption(opt);
    setAgeGroup(group);
    setParentsMustCome(withParents);
    // Haupt-Conversion fürs Marketing: der Lead ist erfasst (Step 1 abgesendet).
    trackEvent('lead', { school: opt?.school_name ?? null, sport: opt?.category_name ?? null });
    setStep({ name: 'paths' });
  };

  const choose = async (path: LeadPath) => {
    if (!leadId) return;
    setDispatchError(null);
    if (path === 'ai_call') {
      setStep({ name: 'ai_call' });
      return;
    }
    setBusy(true);
    try {
      await selectPath(leadId, path);
      if (path === 'callcenter') {
        trackEvent('callback', { kind: 'callcenter', school: option?.school_name ?? null });
        setStep({ name: 'done', variant: 'callcenter' });
      } else {
        setStep({ name: 'booking' });
      }
    } catch (e) {
      setDispatchError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rf-root">
      {isLoading && (
        <div className="rf-center"><Loader2 className="animate-spin" /> Lädt …</div>
      )}

      {isError && (
        <div className="rf-alert rf-alert--error">
          Das Formular konnte nicht geladen werden. Bitte später erneut versuchen.
          <div className="rf-help">{(error as Error)?.message}</div>
        </div>
      )}

      {!isLoading && !isError && options && options.length === 0 && (
        <div className="rf-alert rf-alert--info">
          Aktuell sind keine Standorte für die Online-Anmeldung freigeschaltet.
        </div>
      )}

      {!isLoading && !isError && options && options.length > 0 && (
        <>
          {step.name === 'form' && <LeadForm options={options} onCaptured={handleCaptured} />}

          {step.name === 'paths' && (
            <>
              <div className="rf-eyebrow">Fast geschafft</div>
              <h1 className="rf-title">Wie möchtest du <span className="rf-accent">weitermachen</span>?</h1>
              {dispatchError && <div className="rf-alert rf-alert--error">{dispatchError}</div>}
              <PathSelection option={option} onChoose={choose} busy={busy} />
            </>
          )}

          {step.name === 'booking' && option && leadId && (
            <>
              <div className="rf-eyebrow">Termin buchen</div>
              <h1 className="rf-title">Dein <span className="rf-accent">Probetraining</span> buchen</h1>
              <PathBooking
                leadId={leadId}
                option={option}
                forChild={ageGroup !== null}
                onBooked={({ replacedCount }) => {
                  trackEvent('booking', { school: option.school_name, sport: option.category_name });
                  setStep({ name: 'done', variant: 'booking', replacedCount });
                }}
              />
            </>
          )}

          {step.name === 'ai_call' && leadId && (
            <>
              <div className="rf-eyebrow">KI-Telefonassistent</div>
              <h1 className="rf-title">Wann sollen wir dich <span className="rf-accent">anrufen</span>?</h1>
              <PathAiCall
                leadId={leadId}
                onDispatched={() => {
                  trackEvent('callback', { kind: 'ai_call', school: option?.school_name ?? null });
                  setStep({ name: 'done', variant: 'ai_call' });
                }}
              />
            </>
          )}

          {step.name === 'done' && (
            <ThankYou
              title={
                step.variant === 'booking'
                  ? 'Termin gesichert!'
                  : step.variant === 'ai_call'
                    ? 'Wir rufen dich an!'
                    : 'Wir melden uns bei dir!'
              }
            >
              {step.variant === 'booking' && (
                <p>Dein Probetraining ist gebucht. Wir freuen uns auf dich!</p>
              )}
              {step.variant === 'booking' && (
                <p>
                  Bitte sei <strong>15 Minuten vor Trainingsbeginn</strong> da – für einen kurzen
                  Rundgang. Nach dem Training nehmen wir uns noch ca. 25 Minuten für ein
                  persönliches Beratungsgespräch.
                </p>
              )}
              {step.variant === 'booking' && (step.replacedCount ?? 0) > 0 && (
                <div className="rf-alert rf-alert--info" style={{ marginTop: '0.75rem', textAlign: 'left' }}>
                  Hinweis: Für dich lag bereits{' '}
                  {step.replacedCount === 1
                    ? 'ein früherer Probetermin'
                    : 'ein früherer Probetermin (bzw. mehrere)'}{' '}
                  vor. Dieser neue Termin ist jetzt gültig – der{' '}
                  {step.replacedCount === 1 ? 'vorherige Termin wurde' : 'vorherigen Termine wurden'}{' '}
                  automatisch storniert und der Platz wieder freigegeben.
                </div>
              )}
              {step.variant === 'ai_call' && (
                <p>Unser KI-Telefonassistent meldet sich in Kürze. Halte dein Telefon bereit – falls es nicht klappt, versuchen wir es später erneut.</p>
              )}
              {step.variant === 'callcenter' && (
                <p>Vielen Dank! Unser Team ruft dich zeitnah persönlich zurück, um dein Probetraining zu vereinbaren.</p>
              )}
              {/* Altersregel: gilt am Tag des Probetrainings, nicht am Tag der
                  Anmeldung. Maßgeblich ist der Altersbereich des Kurses; ohne
                  Kurs („Ich bin mir nicht sicher") die gewählte Altersgruppe. */}
              {ageGroup && (
                <div className="rf-alert rf-alert--info" style={{ marginTop: '0.75rem', textAlign: 'left' }}>
                  <strong>Wichtig zum Alter:</strong> Das Probetraining kann nur stattfinden, wenn
                  dein Kind am Tag des Probetrainings{' '}
                  {option && (option.age_min != null || option.age_max != null)
                    ? ageRuleText(option.age_min, option.age_max)
                    : ageRuleText(ageGroup.min, ageGroup.max)}{' '}
                  ist.
                  {parentsMustCome && (
                    <>
                      {' '}
                      Weil dein Kind noch nicht volljährig ist, müssen die Eltern den Vertrag
                      unterschreiben – <strong>bitte kommt gemeinsam</strong> zum Probetraining.
                    </>
                  )}
                </div>
              )}
              <p className="rf-email-note">
                📧 Eine E-Mail mit allen Infos zu deiner Anmeldung ist unterwegs – bitte schau zur Sicherheit auch im Spam-Ordner nach.
              </p>
              <ShareButtons />
            </ThankYou>
          )}
        </>
      )}
    </div>
  );
}
